import type { AttackStyle, MoveDef } from "../data/moves.ts";
import { STATUS_META } from "../data/status.ts";
import { type Gfx, type Rgba } from "../engine/gfx.ts";
import type { StrikeResult } from "../game/combat.ts";
import { hash2 } from "../world/noise.ts";
import { TYPE_COL, UI, withA } from "./pal.ts";

export type AttackFamily = "melee" | "projectile" | "beam" | "status";
export type { AttackStyle };
export interface AttackCue {
  move: MoveDef;
  own: boolean;
  elapsed: number;
  result: StrikeResult | null;
}

const PROJECTILES = new Set([
  "pondPump", "copeSplash", "feelsBad", "panicSell", "diamondBark",
  "airdropFetch", "marketCapsize", "liquidityWave", "cashRain", "hoverTax",
  "confettiBurst", "greenWick", "bagBrine",
  "queueCry", "sootSpit", "slipstream", "ticketToss", "thawDrip",
  "reedDecree", "bottleStorm", "echoFetch", "falseBeacon", "rainFreight",
  "chirpFlood", "reedRattle", "footnote", "dunkSplash", "flickDown", "steamHuff", "copyPaste",
  "polishPuff", "tallTale", "seedToss", "steamPop", "yardShut", "stampede", "lidBlow", "compliment",
  "moodWave", "scratchNote", "marginTap", "leakDrop", "waitDrip", "inkStamp", "contextToss", "replyStorm",
  "snowPuff", "blizzardNod", "curdToss", "gravyWave", "seenOnce", "rantStorm", "dropSauce", "sauceFlood", "pingPing",
  "lookBook", "spiceToss", "midwave", "screenTime", "slushToss",
  "ehEcho", "yawnPuff", "dreamSpill", "laneToss", "mapleDrip", "lagSpike", "loadingLoop",
  "voiceNote", "pondOvation", "frostSip", "bagelSkip", "bakerDozen", "rinkRattle", "clapShot", "linkFling", "bookmarkFall",
]);
const BEAMS = new Set(["laserGlare", "moonHowl", "ringFlash", "chartFlare", "candleBreath", "ventBeam", "brineSiphon", "mintRay",
  "nightSignal", "marshDue", "prismFang", "orbitLance", "canopyFlare",
  "novelDump", "ratioBomb", "bingeSwipe", "faqDump", "hearthRoar",
  "parkDecree", "canonWish",
  "vibeSweep", "threadPost", "plotDump", "counterCall", "ledgerBeam", "montage",
  "unreadBomb", "pingStorm", "alertBlast",
  "slayStorm", "cookOff", "nuanceDump", "lockStorm", "slushFlood",
  "chorusEh", "laneClosure", "syrupSurge", "packetBurst",
  "honkCast", "chillFront", "tabCascade"]);
const FRAME = 1000 / 60;

/** Where each side's creature reads from (body centre in the 384×216 frame). */
export const BODY = {
  you: { x: 104, y: 134 },
  foe: { x: 280, y: 74 },
};

/** Which family each explicit style belongs to (timing and the attacker's pose follow the family). */
const STYLE_FAMILY: Record<AttackStyle, AttackFamily> = {
  strike: "melee", slash: "melee", slam: "melee", flurry: "melee",
  orb: "projectile", volley: "projectile", rain: "projectile", siphon: "projectile",
  beam: "beam", wave: "beam", vortex: "beam",
  hex: "status", boost: "status", shield: "status", heal: "status",
};

export function attackFamily(move: MoveDef): AttackFamily {
  if (move.power === 0) return "status";
  if (move.anim) return move.anim;
  if (move.style) return STYLE_FAMILY[move.style];
  if (BEAMS.has(move.id)) return "beam";
  if (PROJECTILES.has(move.id)) return "projectile";
  return "melee";
}

/**
 * How a move looks: its own style, or one read from its family and effect, so every older move
 * also gets a fitting variant (volleys flurry, drains flow back, heavy blows slam, crits slash).
 */
export function attackStyle(move: MoveDef): AttackStyle {
  const fx = move.fx;
  if (move.power === 0) {
    if (fx?.kind === "heal" || fx?.kind === "cure") return "heal";
    if (fx?.kind === "stats") return fx.def ? "shield" : "boost";
    if (fx?.kind === "stat" && fx.target === "self") return fx.stat === "def" ? "shield" : "boost";
    if (fx?.kind === "surehit") return "boost";
    return "hex";
  }
  if (move.style) return move.style;
  const family = attackFamily(move);
  if (family === "melee") return fx?.kind === "multi" ? "flurry" : move.power >= 100 ? "slam" : move.crit > 0 ? "slash" : "strike";
  if (family === "projectile") return fx?.kind === "multi" ? "volley" : fx?.kind === "drain" ? "siphon" : "orb";
  return fx?.kind === "drain" ? "siphon" : "beam";
}

/** Timings in milliseconds (authored 60 Hz frames, independent of render rate). */
export function attackTiming(move: MoveDef): { impact: number; duration: number } {
  switch (attackFamily(move)) {
    case "melee": return { impact: 16 * FRAME, duration: 34 * FRAME };
    case "beam": return { impact: 18 * FRAME, duration: 38 * FRAME };
    case "projectile": return { impact: 20 * FRAME, duration: 38 * FRAME };
    case "status": return { impact: 16 * FRAME, duration: 36 * FRAME };
  }
}

export function statusOnSelf(move: MoveDef): boolean {
  return move.fx?.kind === "heal" || move.fx?.kind === "surehit" || move.fx?.kind === "cure" || move.fx?.kind === "stats" ||
    (move.fx?.kind === "stat" && move.fx.target === "self");
}

/** Attacker body offset: wind-up, lunge, hold on contact, ease home. Slams hop before they land. */
export function attackPose(cue: AttackCue): { x: number; y: number } {
  const { impact, duration } = attackTiming(cue.move);
  const t = cue.elapsed;
  if (t >= duration) return { x: 0, y: 0 };
  const dir = cue.own ? 1 : -1;
  if (attackFamily(cue.move) !== "melee") {
    // Ranged: a small recoil hop at release.
    return { x: t < impact ? -dir * 3 : 0, y: t < impact ? -2 : 0 };
  }
  let reach: number;
  const wind = 5 * FRAME;
  const hold = 3 * FRAME;
  if (t < wind) reach = -0.1 * Math.sin((t / wind) * Math.PI * 0.5);
  else if (t < impact) {
    const p = (t - wind) / Math.max(FRAME, impact - wind);
    reach = -0.1 + 1.1 * p * p * p;
  } else if (t < impact + hold) reach = 1;
  else {
    const p = (t - impact - hold) / Math.max(FRAME, duration - impact - hold);
    reach = (1 - p) ** 3;
  }
  const hop = attackStyle(cue.move) === "slam" && t < impact ? Math.sin((t / impact) * Math.PI) * 26 : 0;
  return { x: Math.round(dir * 118 * reach), y: Math.round(-dir * 40 * reach - hop) };
}

const flashCache = new WeakMap<ImageData, ImageData>();
/** White silhouette of a sprite (hit flash). */
export function hitSprite(src: ImageData): ImageData {
  const hit = flashCache.get(src);
  if (hit) return hit;
  const img = new ImageData(src.width, src.height);
  for (let i = 0; i < src.data.length; i += 4) {
    img.data[i] = 255; img.data[i + 1] = 255; img.data[i + 2] = 255;
    img.data[i + 3] = src.data[i + 3]!;
  }
  flashCache.set(src, img);
  return img;
}

function star(g: Gfx, x: number, y: number, r: number, c: Rgba): void {
  g.line(x - r, y, x + r, y, c);
  g.line(x, y - r, x, y + r, c);
  const d = Math.max(1, Math.round(r * 0.55));
  g.line(x - d, y - d, x + d, y + d, withA(c, c[3] * 0.7));
  g.line(x - d, y + d, x + d, y - d, withA(c, c[3] * 0.7));
  g.p(x, y, UI.white);
}

function orb(g: Gfx, x: number, y: number, r: number, c: Rgba): void {
  g.glow(x, y, r * 2.4, c, 0.9);
  g.disc(x, y, r, c);
  g.disc(x - r * 0.25, y - r * 0.25, Math.max(1, r * 0.55), UI.white);
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/**
 * One small type-flavoured particle: embers, ice shards, droplets, spores, ping arcs, confetti,
 * tears, sparkles, dark motes, glitch bits, crescents, leaves and wisps. `k` is its remaining life.
 */
function motif(g: Gfx, type: string, px: number, py: number, k: number, i: number, t: number): void {
  const col = TYPE_COL[type] ?? TYPE_COL.chaos!;
  const a = clamp01(k);
  if (a <= 0) return;
  const x = Math.round(px), y = Math.round(py);
  const c = withA(col.fill, 255 * a), hi = withA(col.hi, 255 * a), w = withA(UI.white, 230 * a);
  switch (type) {
    case "burn": g.glow(x, y, 4, col.fill, 0.6 * a); g.fill(x, y, 2, 2, hi); g.p(x, y - 1, w); break;
    case "hodl": g.line(x, y - 3, x + 1, y + 2, hi); g.line(x - 2, y, x + 2, y - 1, c); g.p(x, y - 3, w); break;
    case "liquid": g.disc(x, y, 1.6, c); g.p(x - 1, y - 1, w); g.p(x, y - 3, withA(col.hi, 160 * a)); break;
    case "viral": g.ring(x, y, 2.5, 2.5, c, 1); g.p(x, y, hi); break;
    case "signal": g.ring(x, y, 3, 2, c, 1); g.ring(x, y, 5, 3.5, withA(col.hi, 140 * a), 1); break;
    case "chaos": {
      const pal = [col.fill, UI.gold, UI.pink, col.hi];
      g.fill(x, y, 2, 1, withA(pal[i % 4]!, 255 * a));
      g.fill(x + 1, y + 1, 1, 1, withA(pal[(i + 1) % 4]!, 255 * a));
      break;
    }
    case "cope": g.disc(x, y + 1, 1.4, c); g.p(x, y - 1, hi); g.p(x, y - 2, withA(col.hi, 150 * a)); break;
    case "flex": star(g, x, y, 2 + (i % 2), withA(col.hi, 255 * a)); break;
    case "rug": g.disc(x, y, 1.5, withA(col.rim, 255 * a)); g.p(x + 1, y - 1, c); g.p(x - 2, y + 1, withA(col.hi, 150 * a)); break;
    case "ai": {
      const f = (i + Math.floor(t / 50)) % 3;
      g.fill(x - 1, y, f === 0 ? 4 : 2, 1, f === 1 ? w : c);
      g.fill(x, y + 2, 1, f === 2 ? 2 : 1, hi);
      break;
    }
    case "moon":
      g.p(x, y - 2, hi); g.p(x - 1, y - 1, hi); g.p(x - 1, y, c); g.p(x - 1, y + 1, hi); g.p(x, y + 2, hi);
      g.p(x + 1, y - 2, withA(col.fill, 150 * a));
      break;
    case "stake": g.line(x - 1, y + 1, x + 1, y - 1, c); g.p(x + 1, y - 2, hi); g.p(x - 2, y + 2, withA(col.rim, 255 * a)); break;
    case "rekt":
      g.disc(x, y, 1.8, withA(col.hi, 190 * a));
      g.p(x - 1, y + 2, withA(col.fill, 170 * a)); g.p(x + 1, y + 3, withA(col.fill, 120 * a));
      g.p(x - 1, y - 1, withA(UI.ink, 200 * a));
      break;
    default: g.fill(x, y, 2, 2, c);
  }
}

/** Embers rise, ghosts drift up, droplets and tears fall; everything else settles a little. */
const DRIFT: Record<string, number> = { burn: -26, rekt: -18, moon: -8, liquid: 28, cope: 24, hodl: 12 };

/** Particles of the move's type thrown out from a point, drifting the way that type drifts. */
function burst(g: Gfx, type: string, cx: number, cy: number, after: number, n: number, seed: number, t: number, spread = 1, ground = false): void {
  for (let i = 0; i < n; i++) {
    const a = ground ? (hash2(i, 5, seed) < 0.5 ? Math.PI : 0) + (hash2(i, 7, seed) - 0.5) * 0.9 : hash2(i, 5, seed) * Math.PI * 2;
    const sp = (20 + hash2(i, 6, seed) * 36) * spread;
    const d = sp * Math.min(1, after * 1.4);
    let x = cx + Math.cos(a) * d;
    let y = cy + Math.sin(a) * d * (ground ? 0.25 : 0.8) + (DRIFT[type] ?? 14) * after * after;
    if (type === "stake") x += Math.sin(after * 9 + i) * 3;
    if (ground) y -= Math.sin(Math.min(1, after * 1.6) * Math.PI) * (8 + hash2(i, 8, seed) * 10);
    if (after < 0.95) {
      g.glow(x, y, 4, (TYPE_COL[type] ?? TYPE_COL.chaos!).fill, 0.55 * (1 - after));
      motif(g, type, x, y, 1 - after, i, t);
    }
  }
}

/** How many times a volley landed, read from the result notes (works for live duels too). */
function hitCount(result: StrikeResult | null, move: MoveDef): number {
  const m = result?.notes.join(" ").match(/Hit (\d+) time/);
  if (m) return Number(m[1]);
  return move.fx?.kind === "multi" ? Math.round((move.fx.min + move.fx.max) / 2) : 1;
}

/** Procedural, local effects only. Never consumes gameplay RNG. */
export function drawAttack(g: Gfx, cue: AttackCue): void {
  const family = attackFamily(cue.move);
  const style = attackStyle(cue.move);
  const { impact, duration } = attackTiming(cue.move);
  const t = cue.elapsed;
  const after = Math.max(0, (t - impact) / (duration - impact));
  const type = cue.move.type;
  const col = TYPE_COL[type]!;
  const color: Rgba = col.fill;
  const from = cue.own ? BODY.you : BODY.foe;
  const to = cue.own ? BODY.foe : BODY.you;
  const dir = cue.own ? 1 : -1;
  const missed = cue.result?.hit === false;
  const tx = to.x + (missed ? dir * 26 : 0);
  const ty = to.y + (missed ? -20 : 0);
  const seed = cue.move.id.length * 31 + (cue.own ? 7 : 3);
  const landed = !!cue.result?.hit && cue.result.dmg > 0;

  if (family === "melee") {
    const pose = attackPose(cue);
    if (t > 5 * FRAME && t < impact + 3 * FRAME) {
      // Speed lines trailing the lunge (a slam trails straight down from the hop).
      for (let i = 0; i < 6; i++) {
        const x = from.x + pose.x - dir * (20 + i * 6);
        const y = from.y + pose.y - 18 + i * 7;
        if (style === "slam") g.line(x + dir * 8, y - 14, x + dir * 8, y - 4, i % 2 ? withA(UI.white, 200) : withA(color, 220), 1);
        else g.line(x - dir * (16 + (i % 3) * 8), y + dir * 5, x, y, i % 2 ? withA(UI.white, 200) : withA(color, 220), 1);
      }
    }
    if (landed && style === "slash" && after < 0.7) {
      // Three claw rakes drawn across the target, one after another.
      for (let j = 0; j < 3; j++) {
        const p = clamp01(after * 5 - j * 0.45);
        if (p <= 0) continue;
        const fade = 1 - clamp01((after - 0.35) / 0.35);
        const o = (j - 1) * 8;
        const x0 = tx - dir * 18 + o, y0 = ty - 20, x1 = tx + dir * 14 + o, y1 = ty + 14;
        const xe = x0 + (x1 - x0) * p, ye = y0 + (y1 - y0) * p;
        g.line(x0, y0, xe, ye, withA(color, 230 * fade), 3);
        g.line(x0, y0, xe, ye, withA(UI.white, 255 * fade), 1);
      }
    }
    if (landed && style === "slam") {
      // Ground shockwave at the target's feet, then dust of the move's type.
      const r = 10 + after * 56;
      g.ring(tx, ty + 24, r, r * 0.28, withA(color, 220 * (1 - after)), 2);
      g.ring(tx, ty + 24, r * 0.7, r * 0.2, withA(UI.white, 180 * (1 - after)), 1);
      burst(g, type, tx, ty + 22, after, 12, seed + 9, t, 1.2, true);
    }
    if (landed && style === "flurry") {
      // One quick jab star per landed hit, staggered.
      const n = hitCount(cue.result, cue.move);
      for (let j = 0; j < n; j++) {
        const s0 = j * 0.13, k = (after - s0) / 0.28;
        if (k < 0 || k > 1) continue;
        const x = tx + (hash2(j, 11, seed) - 0.5) * 34, y = ty + (hash2(j, 12, seed) - 0.5) * 30;
        g.glow(x, y, 14 * (1 - k), color, 1.1);
        star(g, Math.round(x), Math.round(y), Math.round(12 * (1 - k)) + 3, withA(UI.white, 250 * (1 - k)));
      }
    }
  } else if (style === "beam" || (family === "beam" && style === "siphon")) {
    if (t < impact) {
      const charge = (t / impact) ** 2;
      g.glow(from.x + dir * 18, from.y - 6, 10 + charge * 14, color, 0.8 * charge);
      for (let i = 0; i < 8; i++) {
        const a = hash2(i, 1, seed) * Math.PI * 2 + t / 120;
        const r = (1 - charge) * 34 + 4;
        g.p(Math.round(from.x + dir * 18 + Math.cos(a) * r), Math.round(from.y - 6 + Math.sin(a) * r * 0.7), i % 2 ? UI.white : color);
      }
    } else if (after < 0.6) {
      const k = 1 - after / 0.6;
      const sx = from.x + dir * 18, sy = from.y - 6;
      const wob = Math.sin(t / 25) * 1.5;
      for (let w = 5; w >= 0; w--) {
        const c: Rgba = w > 3 ? withA(color, 90 * k) : w > 1 ? withA(color, 230 * k) : withA(UI.white, 255 * k);
        g.line(sx, sy - w + wob, tx, ty - w + wob, c);
        g.line(sx, sy + w + wob, tx, ty + w + wob, c);
      }
      g.glow(tx, ty, 26 * k, color, 1.2);
      g.glow(sx, sy, 14 * k, UI.white, 0.8);
    }
  } else if (style === "wave") {
    // Rings of sound or water rolling from the attacker to the target.
    for (let j = 0; j < 4; j++) {
      const p = clamp01((t - j * 3 * FRAME) / Math.max(FRAME, impact - 3 * FRAME));
      if (p <= 0 || (p >= 1 && after > 0.4)) continue;
      const x = from.x + dir * 14 + (tx - from.x - dir * 14) * p, y = from.y - 6 + (ty - from.y + 6) * p;
      const r = 5 + p * 12;
      const fade = p >= 1 ? 1 - after / 0.4 : 1;
      g.ring(x, y, r * 0.55, r * 1.3, withA(j % 2 ? col.hi : color, 220 * fade), 2);
    }
    if (landed && after < 0.7) for (let j = 0; j < 3; j++) {
      const r = 8 + (after * 3 + j * 0.3) % 1 * 30;
      g.ring(tx, ty, r * 0.7, r, withA(color, 200 * (1 - after)), 1.5);
    }
  } else if (style === "vortex") {
    // Motes spiral into the target and collapse into a dark eye.
    const start = impact * 0.2;
    if (t > start && after < 0.75) {
      const life = after > 0 ? 1 - after / 0.75 : clamp01((t - start) / (impact - start));
      g.glow(tx, ty, 22 + 12 * life, withA(col.rim, 255), 1.1 * life);
      g.disc(tx, ty, 6 * life, withA(col.rim, 170 * life));
      for (let arm = 0; arm < 3; arm++) {
        let px = 0, py = 0;
        for (let q = 0; q <= 12; q++) {
          const r = 44 - q * 3.3, a = arm * (Math.PI * 2 / 3) + q * 0.42 + t / 110;
          const x = tx + Math.cos(a) * r, y = ty + Math.sin(a) * r * 0.55;
          if (q) g.line(px, py, x, y, withA(q % 2 ? col.hi : color, 210 * life * (q / 12)), 1);
          px = x; py = y;
        }
      }
      for (let i = 0; i < 16; i++) {
        const phase = ((t / 520) + i / 16) % 1;
        const r = 36 * (1 - phase) + 3;
        const a = hash2(i, 13, seed) * Math.PI * 2 + t / 90;
        motif(g, type, tx + Math.cos(a) * r, ty + Math.sin(a) * r * 0.55, life * (0.4 + phase * 0.6), i, t);
      }
    }
  } else if (style === "rain") {
    // Drops of the move's type falling onto the target from above.
    for (let i = 0; i < 16; i++) {
      const s0 = impact * (0.15 + hash2(i, 14, seed) * 0.7);
      const fall = impact * 0.35;
      const p = (t - s0) / fall;
      if (p < 0 || p > 1.6) continue;
      const x = tx + (hash2(i, 15, seed) - 0.5) * 56 + (1 - Math.min(1, p)) * dir * 10;
      const yEnd = ty + (hash2(i, 16, seed) - 0.5) * 24;
      if (p <= 1) {
        const y = yEnd - 96 * (1 - p);
        g.line(x - dir * 4, y - 14, x, y, withA(col.hi, 170), 2);
        g.glow(x, y, 6, color, 0.8);
        g.disc(x, y, 2.4, withA(color, 240));
        g.p(Math.round(x) - 1, Math.round(y) - 1, UI.white);
        motif(g, type, x + 3, y - 3, 1, i, t);
      } else {
        const k = (p - 1) / 0.6;
        g.ring(x, yEnd + 2, 2 + k * 5, 1 + k * 2, withA(color, 200 * (1 - k)), 1);
      }
    }
  } else if (style === "volley") {
    // Several small shots, each on its own arc, landing in turn.
    const n = Math.max(3, Math.min(5, hitCount(cue.result, cue.move)));
    for (let j = 0; j < n; j++) {
      const s0 = 2 * FRAME + j * 2.5 * FRAME;
      const p = clamp01((t - s0) / Math.max(FRAME, impact - s0));
      if (p <= 0 || (p >= 1 && !(missed && after < 0.4))) continue;
      const arc = 14 + hash2(j, 17, seed) * 22;
      const ox = (hash2(j, 18, seed) - 0.5) * 20, oy = (hash2(j, 19, seed) - 0.5) * 18;
      const x = from.x + (tx + ox - from.x) * p, y = from.y - 6 + (ty + oy - from.y + 6) * p - Math.sin(p * Math.PI) * arc;
      g.disc(x, y, 2.5, withA(color, 230));
      g.p(Math.round(x - 1), Math.round(y - 1), UI.white);
      motif(g, type, x - dir * 5, y + 2, 0.7, j, t);
    }
  } else if (style === "siphon" || family === "projectile") {
    // A shot out; a siphon then pulls orbs of the foe's HP back to the attacker.
    const raw = Math.min(1, Math.max(0, (t - 3 * FRAME) / Math.max(FRAME, impact - 3 * FRAME)));
    const progress = 1 - (1 - raw) ** 2;
    if (t <= impact || (missed && after < 0.5)) {
      const travel = missed ? 1 + after * 0.3 : progress;
      for (let i = 6; i >= 0; i--) {
        const p = Math.max(0, travel - i * 0.05);
        const x = from.x + (tx - from.x) * p;
        const y = from.y - 6 + (ty - from.y + 6) * p - Math.sin(p * Math.PI) * (style === "siphon" ? 10 : 28);
        if (i === 0) orb(g, x, y, style === "siphon" ? 3 : 5, color);
        else g.disc(x, y, Math.max(1, 4 - i * 0.5), withA(i % 2 ? col.hi : color, 200 - i * 24));
      }
    }
  } else {
    const self = statusOnSelf(cue.move);
    const center = self ? from : { x: tx, y: ty };
    const p = Math.min(1, t / duration);
    g.glow(center.x, center.y + 6, 36 * Math.sin(p * Math.PI), color, 0.6);
    if (style === "shield") {
      // A faceted barrier that locks into place around the user.
      const r = 30 - 6 * clamp01(t / impact), k = Math.sin(p * Math.PI);
      const pts: [number, number][] = [[0, -r], [r * 0.8, 0], [0, r * 0.9], [-r * 0.8, 0]];
      for (let i = 0; i < 4; i++) {
        const [ax, ay] = pts[i]!, [bx, by] = pts[(i + 1) % 4]!;
        g.line(center.x + ax, center.y + 4 + ay, center.x + bx, center.y + 4 + by, withA(i % 2 ? col.hi : color, 230 * k), 2);
        star(g, Math.round(center.x + ax), Math.round(center.y + 4 + ay), 2, withA(UI.white, 220 * k));
      }
    }
    if (style === "boost") {
      // Aura lines rising past the user.
      for (let i = 0; i < 6; i++) {
        const x = center.x - 26 + i * 10 + Math.sin(t / 60 + i) * 2;
        const y = center.y + 30 - ((t / 4 + i * 17) % 60);
        g.line(x, y, x, y - 8, withA(i % 2 ? UI.white : col.hi, 200 * Math.sin(p * Math.PI)), 1);
      }
    }
    const heal = style === "heal";
    for (let i = 0; i < 10; i++) {
      const a = hash2(i, 2, seed) * Math.PI * 2 + p * 3;
      const rr = 18 + hash2(i, 3, seed) * 16;
      const up = self ? -1 : 0.5;
      const x = Math.round(center.x + Math.cos(a) * rr);
      const y = Math.round(center.y + 14 + Math.sin(a) * 8 + up * p * 40 * (0.5 + hash2(i, 4, seed)));
      const c = i % 3 ? color : UI.white;
      if (heal) { g.fill(x - 2, y, 5, 1, UI.green); g.fill(x, y - 2, 1, 5, UI.green); }
      else if (self) { g.line(x - 2, y + 2, x, y, c); g.line(x, y, x + 2, y + 2, c); }
      else { g.line(x - 2, y - 2, x, y, c); g.line(x, y, x + 2, y - 2, c); }
    }
    if (cue.move.fx?.kind === "cure" && t > impact) {
      const r = 10 + after * 30;
      g.ring(center.x, center.y + 4, r, r * 0.8, withA(UI.white, 220 * (1 - after)), 1.5);
    }
  }

  if (!cue.result) return;
  if (missed) {
    for (let i = 0; i < 3; i++) {
      const x = tx + dir * Math.round(after * 20) - i * 4;
      g.line(x, ty + i * 7, x + dir * 10, ty + i * 7 - 3, withA(UI.white, 200 * (1 - after)));
    }
    popText(g, to.x, to.y - 40, "MISS", UI.textDim, after);
    return;
  }
  if (cue.result.dmg > 0) {
    // Impact flash, shock ring and a burst of the move's own particles.
    const own = style === "slash" || style === "flurry" || style === "rain" || style === "vortex" || style === "siphon" || style === "wave" || style === "volley";
    if (after < 0.25) g.glow(to.x, to.y, style === "slam" ? 52 : own ? 24 : 40, UI.white, (own ? 0.9 : 1.4) * (1 - after / 0.25));
    const ring = 6 + after * 34;
    g.ring(to.x, to.y + 4, ring, ring * 0.7, withA(UI.white, 180 * (1 - after)), 2);
    burst(g, type, to.x, to.y, after, cue.result.crit ? 18 : 12, seed, t);
    if (after < 0.35 && style !== "flurry") star(g, Math.round(to.x), Math.round(to.y), Math.round((cue.result.crit ? 22 : 15) * (1 - after * 2)), withA(UI.white, 240));
    if (style === "siphon" && after < 0.9) {
      // HP orbs flowing home to the attacker.
      for (let j = 0; j < 7; j++) {
        const p = clamp01(after * 1.6 - j * 0.09);
        if (p <= 0 || p >= 1) continue;
        const x = to.x + (from.x - to.x) * p, y = to.y + (from.y - 6 - to.y) * p - Math.sin(p * Math.PI) * (18 + j * 3);
        g.glow(x, y, 5, UI.green, 0.8);
        g.disc(x, y, 1.8, withA(col.hi, 230));
      }
      if (after > 0.55) for (let j = 0; j < 4; j++) {
        const x = from.x - 14 + j * 9, y = from.y - 4 - (after - 0.55) * 50 - (j % 2) * 6;
        g.fill(x - 2, y, 5, 1, withA(UI.green, 255 * (1 - after)));
        g.fill(x, y - 2, 1, 5, withA(UI.green, 255 * (1 - after)));
      }
    }
    popText(g, to.x + dir * 6, to.y - 34, `-${cue.result.dmg}`, cue.result.crit ? UI.goldHi : UI.white, after, 2);
    if (cue.result.crit) popText(g, to.x, to.y - 56, "CRITICAL!", UI.gold, after);
    const eff = cue.result.notes.find((n) => /effective|No bite/i.test(n));
    if (eff && /Super/i.test(eff)) popText(g, to.x + dir * 44, to.y - 20, "SUPER!", UI.pink, after);
    else if (eff && /Not very/i.test(eff)) popText(g, to.x + dir * 44, to.y - 20, "resisted", UI.textDim, after);
  }
  // A condition that just landed flashes its own colour and chip over the target.
  const fx = cue.move.fx;
  if (fx?.kind === "status" && cue.result.notes.some((n) => n.endsWith(`${STATUS_META[fx.status].verb}!`))) {
    const sc = STATUS_META[fx.status].col;
    const r = 14 + after * 26;
    g.ring(tx, ty, r, r * 0.75, withA(sc, 230 * (1 - after)), 2);
    popText(g, to.x - dir * 40, to.y - 28, STATUS_META[fx.status].chip, withA(sc, 255), after);
  }
}

/** Floating readout that pops, rises and fades. */
function popText(g: Gfx, cx: number, y: number, s: string, c: Rgba, t: number, scale = 1): void {
  if (t >= 1) return;
  const rise = Math.round(t * 14);
  const pop = t < 0.15 ? 1 + (0.15 - t) * 3 : 1;
  const sc = scale > 1 && pop > 1.2 ? scale + 1 : scale;
  const w = g.textW(s, sc);
  const a = t > 0.7 ? (1 - t) / 0.3 : 1;
  g.textOutline(Math.round(cx - w / 2), Math.round(y - rise), s, withA(c, 255 * a), withA(UI.ink, 255 * a), sc);
}
